import { useMemo, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Users,
  GraduationCap,
  ArrowRight,
  Loader2,
  CheckCircle2,
  User,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { toast } from "react-toastify";
import clientService from "../../api/ClientService";
import { mapClientData } from "../../utils/clientHelpers";
import Loader from "../../components/misc/Loader";

function ClientCard({ client, isSelected, onClick, type = "client" }) {
  const fullName = client.fullName || `${client.firstName || ""} ${client.lastName || ""}`.trim();
  const displayPhone = client.phone || client.phone_number || "";
  const displayEmail = client.email || "";

  return (
    <motion.button
      type="button"
      whileHover={{ y: -2, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative rounded-xl border text-left shadow-sm transition-all duration-300 focus:outline-none w-full ${
        isSelected
          ? "ring-2 ring-emerald-500 bg-emerald-50 border-emerald-300"
          : "ring-1 ring-gray-200 bg-white hover:shadow-lg hover:ring-2 hover:ring-emerald-300"
      }`}
    >
      <div className="p-4 flex gap-3 items-center">
        <div className={`flex-shrink-0 flex justify-center items-center w-12 h-12 rounded-full ${
          isSelected ? "bg-emerald-500" : type === "student" ? "bg-blue-100" : "bg-gray-100"
        }`}>
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-white" />
          ) : type === "student" ? (
            <GraduationCap className="w-6 h-6 text-blue-600" />
          ) : (
            <User className="w-6 h-6 text-gray-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-base font-semibold truncate ${isSelected ? "text-emerald-900" : "text-gray-900"}`}>
            {fullName}
          </h4>
          <div className="mt-1 space-y-0.5">
            {displayPhone && (
              <p className="text-sm text-gray-600 truncate flex items-center gap-1">
                <Phone size={12} /> {displayPhone}
              </p>
            )}
            {displayEmail && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <Mail size={12} /> {displayEmail}
              </p>
            )}
            {type === "student" && client.schoolName && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <GraduationCap size={12} /> {client.schoolName}
              </p>
            )}
            {(client.region || client.location) && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <MapPin size={12} /> {[client.region, client.location].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>
        {isSelected && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex-shrink-0">
            <div className="flex justify-center items-center w-6 h-6 bg-emerald-500 rounded-full">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
          </motion.div>
        )}
      </div>
    </motion.button>
  );
}

const ITEMS_PER_PAGE = 9;

export default function SelectClientStep({ onClientSelected, onBack }) {
  const [clientType, setClientType] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const response = await clientService.getClients();
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : (response.data?.data ?? []);
      const mapped = Array.isArray(list) ? list.map(mapClientData) : [];
      setClients(response.status && mapped.length >= 0 ? mapped : []);
    } catch (error) {
      toast.error("Failed to fetch clients");
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const response = await clientService.getStudents();
      const list = response.data ?? [];
      const mapped = Array.isArray(list)
        ? list.map((s) => ({
            ...s,
            id: s.slug || s.id,
            slug: s.slug,
            firstName: s.first_name || "",
            lastName: s.last_name || "",
            fullName: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
            email: s.email || "",
            phone: s.phone_number || "",
            phone_number: s.phone_number || "",
            region: s.region || "",
            location: s.location || "",
            schoolName: s.school_name || "",
          }))
        : [];
      setStudents(response.status ? mapped : []);
    } catch (error) {
      toast.error("Failed to fetch students");
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  useEffect(() => {
    if (clientType === "clients") fetchClients();
    else if (clientType === "students") fetchStudents();
  }, [clientType, fetchClients, fetchStudents]);

  const filteredItems = useMemo(() => {
    const items = clientType === "clients" ? clients : students;
    if (!debouncedSearch.trim()) return items;
    const q = debouncedSearch.toLowerCase();
    return items.filter((item) => {
      const fullName = item.fullName || `${item.firstName || ""} ${item.lastName || ""}`.trim();
      return (
        fullName.toLowerCase().includes(q) ||
        (item.firstName || "").toLowerCase().includes(q) ||
        (item.lastName || "").toLowerCase().includes(q) ||
        (item.email || "").toLowerCase().includes(q) ||
        (item.phone || item.phone_number || "").includes(q) ||
        (item.region || "").toLowerCase().includes(q) ||
        (item.location || "").toLowerCase().includes(q) ||
        (item.schoolName || "").toLowerCase().includes(q)
      );
    });
  }, [clientType, clients, students, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, currentPage]);

  const handleClientTypeSelect = (type) => {
    setClientType(type);
    setSelectedClient(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleContinue = () => {
    if (!selectedClient) {
      toast.error("Please select a client or student");
      return;
    }
    onClientSelected({
      client: selectedClient,
      clientType: clientType === "students" ? "students" : "clients",
    });
  };

  if (!clientType) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-white rounded-2xl ring-1 ring-gray-200"
      >
        {onBack && (
          <div className="mb-6">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              ← Back
            </button>
          </div>
        )}
        <div className="text-center mb-8">
          <h3 className="mb-2 text-2xl font-bold text-gray-900">Who is this registration for?</h3>
          <p className="text-sm text-gray-600">
            Select whether you're registering for a regular client or a student
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-2xl mx-auto">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleClientTypeSelect("clients")}
            className="relative p-6 text-left bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 transition-all group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-emerald-500 rounded-full group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="mb-2 text-xl font-bold text-gray-900">Regular Clients</h4>
              <p className="text-sm text-gray-600">Register for your registered regular clients</p>
            </div>
            <div className="absolute top-4 right-4">
              <ArrowRight className="w-5 h-5 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </motion.button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleClientTypeSelect("students")}
            className="relative p-6 text-left bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 transition-all group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-blue-500 rounded-full group-hover:scale-110 transition-transform">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h4 className="mb-2 text-xl font-bold text-gray-900">Students</h4>
              <p className="text-sm text-gray-600">Register for your registered students</p>
            </div>
            <div className="absolute top-4 right-4">
              <ArrowRight className="w-5 h-5 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 bg-white rounded-2xl ring-1 ring-gray-200"
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
            Select {clientType === "students" ? "Student" : "Client"}
          </h3>
          <div className="mt-2 w-28 h-1 bg-emerald-500 rounded" />
          <p className="mt-2 text-sm text-gray-600">
            Choose a {clientType === "students" ? "student" : "client"} for this event registration
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setClientType(null);
            setSelectedClient(null);
            if (onBack) onBack();
          }}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          ← Back
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
          <input
            type="text"
            placeholder={`Search ${clientType === "students" ? "students" : "clients"} by name, phone, email...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="py-3 pr-4 pl-10 w-full text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>

      {(clientType === "clients" && loadingClients) || (clientType === "students" && loadingStudents) ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col gap-4 items-center">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-sm text-gray-600">Loading {clientType === "students" ? "students" : "clients"}...</p>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col gap-4 justify-center items-center py-12 text-center">
          <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No {clientType === "students" ? "students" : "clients"} found</h3>
          <p className="text-sm text-gray-600">
            {searchQuery ? "Try adjusting your search" : `No ${clientType === "students" ? "students" : "clients"} available. Register one first.`}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((item) => (
              <ClientCard
                key={item.id || item.slug}
                client={item}
                isSelected={selectedClient?.id === item.id || selectedClient?.slug === item.slug}
                onClick={() => setSelectedClient(item)}
                type={clientType === "students" ? "student" : "client"}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap justify-between items-center mt-8 gap-3">
              <p className="text-sm text-gray-600">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="pt-6 mt-6 border-t border-gray-200"
            >
              <button
                type="button"
                onClick={handleContinue}
                className="w-full px-6 py-3 text-base font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Continue to registration
              </button>
            </motion.div>
          )}
        </>
      )}
    </motion.div>
  );
}
